import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import Button from '../shared/Button'
import ResetButton from '../shared/ResetButton'
import { HelmetLookupFormData } from '../../types/helmetLookup'
import { useHelmetLookup } from '../../hooks/useHelmetLookup'
import { useHelmetTransfer } from '../../hooks/useHelmetTransfer'
import { HelmetInfoCard, DisambiguationPanel, TransferConfirmation, TransferSuccess } from './helmetLookup'

/**
 * Helmet Lookup Tool - Direct helmet identification and transfer component
 * 
 * This component provides helmet lookup functionality by RFID label or code,
 * with support for disambiguation when multiple helmets match, and transfer
 * capabilities to different organizations.
 * 
 * Features:
 * - Real-time RFID format validation
 * - Multi-result disambiguation workflow
 * - Progressive disclosure UI
 * - Transfer execution with confirmation
 * 
 */

const HelmetLookupTool: React.FC = () => {
  // Form state management
  const { register, handleSubmit, formState: { errors }, watch, setValue, reset, clearErrors } = useForm<HelmetLookupFormData>({
    defaultValues: {
      helmetId: '',
      targetOrgID: '',
      targetTeamID: '',
      confirmTransfer: false
    }
  })

  const watchedHelmetId = watch('helmetId')
  const watchedTargetOrgID = watch('targetOrgID')
  const watchedTargetTeamID = watch('targetTeamID')

  // Lookup hook
  const lookup = useHelmetLookup({
    watchedHelmetId,
    watchedTargetOrgID,
    watchedTargetTeamID,
    setValue,
    onLookupStart: () => transfer.setTransferSuccess(null)
  })

  // Transfer hook
  const transfer = useHelmetTransfer({
    helmetInfo: lookup.helmetInfo,
    organizations: lookup.organizations,
    availableTargetTeams: lookup.availableTargetTeams,
    convertibleValidation: lookup.convertibleValidation,
    sfEchoValidation: lookup.sfEchoValidation,
    setError: lookup.setError,
    resetLookupState: lookup.resetLookupState,
    resetForm: reset
  })

  // Reset confirmTransfer and dependent selections when a previous step changes
  useEffect(() => {
    // When helmetId changes, reset org, team, and confirmTransfer
    setValue('targetOrgID', '', { shouldValidate: false })
    setValue('targetTeamID', '', { shouldValidate: false })
    setValue('confirmTransfer', false, { shouldValidate: false })
    clearErrors(['targetOrgID', 'targetTeamID', 'confirmTransfer'])
  }, [watchedHelmetId])

  useEffect(() => {
    // When org changes, reset team and confirmTransfer
    setValue('targetTeamID', '', { shouldValidate: false })
    setValue('confirmTransfer', false, { shouldValidate: false })
    clearErrors(['targetTeamID', 'confirmTransfer'])
  }, [watchedTargetOrgID])

  useEffect(() => {
    // When team changes, reset confirmTransfer
    setValue('confirmTransfer', false, { shouldValidate: false })
    clearErrors(['confirmTransfer'])
  }, [watchedTargetTeamID])

  const handleReset = () => {
    // Reset form data
    reset()
    
    // Clear all states
    lookup.resetLookupState()
    transfer.resetTransferState()
  }

  return (
    <div className="w-full max-w-7xl mx-auto px-2 py-2 sm:px-4 sm:py-4 lg:px-6 lg:py-6 bg-neutral-50 min-h-screen">
      {/* Riddell Header */}
      <div className="bg-riddell-red text-white rounded-t-lg shadow-lg mb-3 sm:mb-4 lg:mb-6 w-full">
        <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-6">
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4 space-y-2 sm:space-y-0 w-full">
            <div className="bg-white p-1.5 sm:p-2 rounded flex-shrink-0">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-riddell-red rounded flex items-center justify-center">
                <span className="text-white font-bold text-sm sm:text-lg">R</span>
              </div>
            </div>
            <div className="text-center sm:text-left flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl lg:text-3xl font-heading font-black tracking-tight">HELMET LOOKUP</h1>
              <p className="text-riddell-red-100 text-sm sm:text-base lg:text-lg font-medium">Direct Helmet Identification & Transfer</p>
            </div>
            {/* Reset Button */}
            <ResetButton
              isVisible={!!(lookup.showHelmetInfo || lookup.showDisambiguation || transfer.transferSuccess)}
              onReset={handleReset}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-xl border border-neutral-200 w-full overflow-visible">
        <div className="p-3 sm:p-4 lg:p-6 w-full">
        
        <form onSubmit={handleSubmit(transfer.handleTransferSubmit)} className="space-y-4 sm:space-y-6 lg:space-y-8 w-full">
                  {/* Always show debug info for helmetInfo and helmetToUpdate if helmetInfo exists */}
                  {/* ...existing code... */}
          {/* Helmet ID Input Section */}
          <div className="bg-neutral-50 border-l-4 border-riddell-red p-3 sm:p-4 lg:p-6 rounded-lg w-full">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3 sm:mb-4">
              <div className="bg-riddell-red text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center font-bold text-xs sm:text-sm lg:text-base flex-shrink-0">1</div>
              <h2 className="text-base sm:text-lg lg:text-xl font-heading font-bold text-riddell-black">RFID INPUT & HELMET LOOKUP</h2>
            </div>
            
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start">
              <div className="flex-1 w-full">
                <label htmlFor="helmetId" className="block text-xs sm:text-sm font-bold text-riddell-black mb-2 uppercase tracking-wide">
                  RFID Label or Code
                </label>
                <input
                  {...register('helmetId', { required: 'Helmet ID is required' })}
                  type="text"
                  id="helmetId"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150"
                  placeholder="Example: 24-1234 or ABC123"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      lookup.handleHelmetLookup();
                    }
                  }}
                />
                {errors.helmetId && (
                  <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
                    <span className="mr-1">⚠</span>
                    {errors.helmetId.message}
                  </p>
                )}
              </div>
              
              <div className="w-full md:w-auto">
                <div className="md:mt-6">
                  <Button
                    type="button"
                    onClick={lookup.handleHelmetLookup}
                    disabled={!lookup.validationResult.valid || lookup.isLookingUp}
                    variant="primary"
                    className="w-full md:w-auto px-4 sm:px-6 py-2.5 sm:py-3 text-base sm:text-lg font-bold uppercase tracking-wide bg-riddell-red text-white border-2 border-riddell-red rounded-md hover:bg-riddell-red-dark disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    {lookup.isLookingUp ? (
                      <div className="flex items-center justify-center space-x-2 sm:space-x-3">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                        <span>LOOKING UP...</span>
                      </div>
                    ) : (
                      'LOOKUP'
                    )}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Validation feedback */}
            <div className="mt-3">
              {lookup.validationResult.valid ? (
                <p className="text-success text-sm font-medium flex items-center">
                  <span className="mr-1">✓</span>
                  Valid {lookup.validationResult.format} format detected
                </p>
              ) : watchedHelmetId.trim() ? (
                <p className="text-error text-sm font-medium flex items-center">
                  <span className="mr-1">✗</span>
                  Invalid format. Use RFID Label (YY-####) or alphanumeric code
                </p>
              ) : null}
            </div>
            
            {/* Error display */}
            {lookup.error && (
              <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm font-medium" role="alert">
                {lookup.error}
              </div>
            )}
          </div>

          {/* Disambiguation Section */}
          {lookup.showDisambiguation && lookup.multipleResults.length > 0 && (
            <DisambiguationPanel
              watchedHelmetId={watchedHelmetId}
              disambiguationOptions={lookup.disambiguationOptions}
              onSelect={lookup.handleDisambiguationSelect}
            />
          )}

          {/* Helmet Information Display */}
          {lookup.showHelmetInfo && lookup.helmetInfo && (
            <HelmetInfoCard helmetInfo={lookup.helmetInfo} />
          )}

          {/* Target Organization & Team Selection + Confirmation */}
          {lookup.showHelmetInfo && lookup.helmetInfo && (
            <TransferConfirmation
              helmetInfo={lookup.helmetInfo}
              organizations={lookup.organizations}
              availableTargetOrgs={lookup.availableTargetOrgs}
              availableTargetTeams={lookup.availableTargetTeams}
              isLoadingOrgs={lookup.isLoadingOrgs}
              isLoadingTargetTeams={lookup.isLoadingTargetTeams}
              isSubmitting={transfer.isSubmitting}
              convertibleValidation={lookup.convertibleValidation}
              isValidatingConvertible={lookup.isValidatingConvertible}
              sfEchoValidation={lookup.sfEchoValidation}
              isValidatingSFEcho={lookup.isValidatingSFEcho}
              watchedTargetOrgID={watchedTargetOrgID}
              watchedTargetTeamID={watchedTargetTeamID}
              errors={errors}
              register={register}
              setValue={setValue}
              watch={watch}
            />
          )}

          {/* Transfer Success Display */}
          {transfer.transferSuccess && (
            <TransferSuccess transferSuccess={transfer.transferSuccess} />
          )}

          {/* TODO: Add transfer confirmation */}
          
        </form>
        </div>
      </div>
    </div>
  )
}

export default HelmetLookupTool
